import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { Header } from "../components/landing/Header";
import { Seo } from "../components/Seo";
import { Footer } from "../components/landing/Footer";
import { articles } from "../data/articles";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = articles[slug];
  if (!article) return <Navigate to="/" replace />;

  return (
    <div className="bg-[#05071F] text-white min-h-screen font-body">
      <Seo title={`${article.title} | Silent Sentry Insights`} description={article.intro.slice(0, 158)} />
      <Header />
      <main className="pt-16">
        <section data-testid="article-hero" className="border-b border-white/10 bg-[#0B1035]">
          <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
            <Link to="/#resources" data-testid="back-to-resources" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" /> All Insights
            </Link>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-violet-400 mb-4">{article.tag}</p>
            <h1 data-testid="article-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium leading-tight">{article.title}</h1>
            <p className="mt-6 flex items-center gap-4 text-sm text-slate-500">
              <span>{article.date}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {article.readTime}</span>
              <span>Silent Sentry Advisory Practice</span>
            </p>
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-6">
          <img src={article.img} alt={article.title} className="w-full h-64 sm:h-80 object-cover -mt-0 mt-12 border border-white/10" />
          <article data-testid="article-body" className="py-12">
            <p className="text-lg text-slate-300 leading-relaxed border-l-2 border-violet-400 pl-6">{article.intro}</p>
            {article.sections.map((s, i) => (
              <section key={s.heading} className="mt-12">
                <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium">
                  <span className="font-mono text-base text-violet-400 mr-3">{String(i + 1).padStart(2, "0")}</span>
                  {s.heading}
                </h2>
                <p className="mt-4 text-slate-400 leading-relaxed">{s.body}</p>
              </section>
            ))}
            <div data-testid="article-takeaways" className="mt-16 border border-white/10 bg-[#0B1035] p-8">
              <h3 className="font-heading text-xl font-medium">Key takeaways</h3>
              <ul className="mt-4 space-y-3">
                {article.takeaways.map((t) => (
                  <li key={t} className="flex gap-3 text-sm text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>

        <section className="py-16 md:py-24 text-center border-t border-white/10 bg-[#0B1035]">
          <div className="max-w-2xl mx-auto px-6">
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium">Want this analysis applied to your environment?</h2>
            <Link to="/intake" data-testid="article-intake-cta"
              className="mt-6 inline-flex items-center gap-2 bg-cyan-400 text-[#05071F] font-semibold px-7 py-3.5 rounded-full hover:bg-cyan-300 transition-colors">
              Request a Confidential Assessment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
